import React, { useState } from 'react';
import { Badge } from 'flowbite-react';
import { HiLocationMarker, HiPencil, HiTrash, HiCheck, HiDuplicate } from 'react-icons/hi';
import type { PersonAddressResponse } from '../../types/api';

interface AddressBadgeProps {
  address: PersonAddressResponse;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

export const AddressBadge: React.FC<AddressBadgeProps> = ({
  address,
  onEdit,
  onDelete,
  canEdit = false
}) => {
  const [copied, setCopied] = useState(false);

  // Format address for display
  const formatAddress = () => {
    const parts: string[] = [];

    // Street address
    if (address.streetNumberName) {
      parts.push(address.streetNumberName);
    }
    if (address.apartmentRoomSuiteNumber) {
      parts.push(address.apartmentRoomSuiteNumber);
    }

    // City, State ZIP
    const cityStateZip: string[] = [];
    if (address.city) cityStateZip.push(address.city);
    if (address.stateAbbreviation) cityStateZip.push(address.stateAbbreviation);
    if (address.postalCode) cityStateZip.push(address.postalCode);

    if (cityStateZip.length > 0) {
      // Format as "City, ST 12345"
      const [city, state, zip] = [address.city, address.stateAbbreviation, address.postalCode];
      if (city && state && zip) {
        parts.push(`${city}, ${state} ${zip}`);
      } else {
        parts.push(cityStateZip.join(', '));
      }
    }

    return parts;
  };

  // Single-line format for copying
  const formatAddressCopy = () => {
    const parts: string[] = [];
    if (address.streetNumberName) parts.push(address.streetNumberName);
    if (address.apartmentRoomSuiteNumber) parts.push(address.apartmentRoomSuiteNumber);
    if (address.city) parts.push(address.city);
    if (address.stateAbbreviation) parts.push(address.stateAbbreviation);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.join(', ');
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(formatAddressCopy()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format date range
  const formatDateRange = () => {
    if (!address.beginDate && !address.endDate) return null;

    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };

    if (address.beginDate && address.endDate) {
      return `${formatDate(address.beginDate)} - ${formatDate(address.endDate)}`;
    } else if (address.beginDate) {
      return `Since ${formatDate(address.beginDate)}`;
    } else if (address.endDate) {
      return `Until ${formatDate(address.endDate)}`;
    }
    return null;
  };

  const addressParts = formatAddress();
  const dateRange = formatDateRange();

  return (
    <div className="flex items-start gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors group">
      {/* Icon */}
      <div className="mt-0.5">
        <HiLocationMarker className="w-5 h-5 text-purple-600 dark:text-purple-400" />
      </div>

      {/* Address content - clickable to copy */}
      <div
        className="flex-1 cursor-pointer"
        onClick={handleCopyToClipboard}
        title="Click to copy address"
      >
        <div className="text-sm text-gray-800 dark:text-gray-200">
          {addressParts.map((part, index) => (
            <div key={index}>{part}</div>
          ))}
        </div>

        {/* County */}
        {address.nameOfCounty && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {address.nameOfCounty} County
          </div>
        )}

        {/* Date range and current badge */}
        <div className="flex items-center gap-2 mt-2">
          {address.isCurrent ? (
            <Badge size="xs" color="success">Current</Badge>
          ) : (
            <Badge size="xs" color="gray">Previous</Badge>
          )}
          {dateRange && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{dateRange}</span>
          )}
        </div>
      </div>

      {/* Copy indicator */}
      <div className="mt-1">
        {copied ? (
          <HiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
        ) : (
          <HiDuplicate className="w-4 h-4 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>

      {/* Edit/Delete buttons - only show if user can edit */}
      {canEdit && (
        <div className="flex items-center gap-1 ml-1 border-l border-gray-300 dark:border-gray-600 pl-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Edit address"
              aria-label="Edit address"
            >
              <HiPencil className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Delete address"
              aria-label="Delete address"
            >
              <HiTrash className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
